<template>
  <div class="p-4 sm:p-6 md:p-8">
    <div class="max-w-7xl mx-auto">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold text-orchestra-blue">
          {{ pageTitle }} - Generated PDF Reports
        </h1>
        <router-link
          to="/admin/form-records"
          class="text-sm text-orchestra-primary hover:text-orchestra-accent hover:underline transition duration-150 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 mr-1">
            <path fill-rule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.56l2.72 2.72a.75.75 0 1 1-1.06 1.06l-4-4a.75.75 0 0 1 0-1.06l4-4a.75.75 0 0 1 1.06 1.06L5.56 9.25H16.25A.75.75 0 0 1 17 10Z" clip-rule="evenodd" />
          </svg>
          Back to Form Categories
        </router-link>
      </div>

      <div v-if="loading" class="text-center py-10">
        <p class="text-lg text-gray-600">Loading PDF reports...</p>
      </div>
      <div v-else-if="error" class="text-center py-10">
        <p class="text-lg text-red-500">Error loading reports: {{ error }}</p>
      </div>
      <div v-else-if="pdfFiles.length === 0" class="text-center py-10 bg-white shadow-md rounded-lg p-6">
        <p class="text-lg text-gray-600">No PDF reports found for this form category.</p>
        <p class="text-sm text-gray-500 mt-2">PDFs will appear here after they are generated from the submissions list.</p>
      </div>

      <div v-else class="space-y-6">
        <div v-for="file in pdfFiles" :key="file.fullPath" class="bg-white shadow-xl rounded-lg p-4 sm:p-6">
          <div class="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
            <div class="flex-grow">
              <h3 class="text-lg font-semibold text-orchestra-primary">
                Reference ID: <span class="font-mono text-gray-700">{{ file.displayId }}</span>
              </h3>
              <!-- Raw Submission ID line removed/commented out -->
              <p class="text-sm text-gray-600">User: {{ file.userName || 'N/A' }} <span v-if="file.userEmail">({{ file.userEmail }})</span></p>
              <p class="text-sm text-gray-500">Generated: {{ file.generatedAt ? formatDate(file.generatedAt) : 'Unknown' }}</p>
            </div>
            <div v-if="!file.downloadUrl && !file.urlError" class="flex-shrink-0 ml-4">
              <button @click="loadPdfUrl(file)" title="Load PDF Preview" class="p-2 text-orchestra-primary hover:text-orchestra-accent rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-orchestra-accent">
                <img src="/images/icons/pdf-file-svgrepo-com.svg" alt="Load PDF" class="w-8 h-8"> 
              </button>
            </div>
          </div>
          
          <div v-if="file.downloadUrl" class="pdf-embed-container mt-2">
            <iframe :src="file.downloadUrl" width="100%" height="700px" style="border: 1px solid #e5e7eb;">
              Your browser does not support iframes. <a :href="file.downloadUrl" target="_blank" class="text-orchestra-primary hover:underline">Download PDF</a>.
            </iframe>
          </div>
          <div v-else-if="file.urlError" class="text-red-500 text-sm mt-2">
            Could not load PDF preview: {{ file.urlError }}
             You can <a :href="file.downloadUrlFallback" @click.prevent="attemptDownload(file.fullPath)" target="_blank" class="text-orchestra-primary hover:underline">try to download it</a>.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'; 
import { useRoute } from 'vue-router';
import { storage as fbStorage } from '@/firebaseConfig'; 
import { ref as storageRef, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { doc, getDoc, db } from '@/firebaseConfig'; 

const route = useRoute();
const formIdentifier = ref(route.params.formIdentifier);
const pdfFiles = ref([]);
const loading = ref(true);
const error = ref(null);

const pageTitle = computed(() => { 
  if (!formIdentifier.value) return 'Unknown Form';
  return formIdentifier.value.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
});

// eslint-disable-next-line no-unused-vars
const formatDate = (isoString) => { 
  if (!isoString) return 'N/A';
  return new Date(isoString).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const fetchPdfReports = async () => {
  loading.value = true; error.value = null; pdfFiles.value = [];
  if (!formIdentifier.value) { error.value = "Form identifier is missing."; loading.value = false; return; }
  const basePath = `generatedPdfs/${formIdentifier.value}/`;
  const listRef = storageRef(fbStorage, basePath);
  try {
    const res = await listAll(listRef);
    const filesDataPromises = res.prefixes.map(async (submissionFolderRef) => {
      const submissionId = submissionFolderRef.name; // This is the Firestore submission ID
      const reportFileRef = storageRef(fbStorage, `${submissionFolderRef.fullPath}/report-${submissionId}.pdf`);
      
      try {
        const metadata = await getMetadata(reportFileRef);
        const customMeta = metadata.customMetadata || {};
        
        let userName = 'N/A';
        let userEmail = '';
        let humanReadableId = submissionId; // Fallback to submissionId initially

        // Fetch humanReadableId and potentially userId from the submission document itself
        const actualSubmissionIdFromMeta = customMeta.submissionId || submissionId;
        const userIdFromMeta = customMeta.userId;

        try {
            const submissionDocSnap = await getDoc(doc(db, "onboardingFormSubmissions", actualSubmissionIdFromMeta));
            if (submissionDocSnap.exists()) {
                const submissionDetails = submissionDocSnap.data();
                if (submissionDetails.humanReadableId) {
                    humanReadableId = submissionDetails.humanReadableId;
                }
                // Use userId from submission doc if not in customMeta or if more reliable
                const effectiveUserId = submissionDetails.userId || userIdFromMeta; 
                if (effectiveUserId && effectiveUserId !== 'N/A') {
                    const userProfileSnap = await getDoc(doc(db, "userProfiles", effectiveUserId));
                    if (userProfileSnap.exists()) {
                        const profile = userProfileSnap.data();
                        userName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'N/A';
                        userEmail = profile.email || '';
                    } else { userName = 'Profile N/A'; }
                }
            } else {
                console.warn(`Submission document ${actualSubmissionIdFromMeta} not found for humanReadableId.`);
            }
        } catch (subError) {
            console.warn("Could not fetch submission details:", subError);
        }
        
        return { 
          name: metadata.name, 
          fullPath: metadata.fullPath, 
          submissionId: actualSubmissionIdFromMeta, // The actual Firestore submission ID
          displayId: humanReadableId, // The ID to show in the UI
          generatedAt: customMeta.generatedAt || metadata.timeCreated, 
          userName: userName, 
          userEmail: userEmail,
          downloadUrl: null, 
          urlError: null,
          downloadUrlFallback: `https://storage.googleapis.com/${fbStorage.app.options.storageBucket}/${metadata.fullPath}` 
        };
      } catch (fileError) { 
        console.warn(`Metadata error for report in folder: ${submissionFolderRef.fullPath}`, fileError); 
        return null; 
      }
    });
    
    const resolvedFilesData = (await Promise.all(filesDataPromises)).filter(file => file !== null);
    
    // Sort by humanReadableId (displayId) if it exists
    pdfFiles.value = resolvedFilesData.sort((a, b) => {
        if (a.displayId && b.displayId && a.displayId.startsWith('YDS-') && b.displayId.startsWith('YDS-')) {
            const numA = parseInt(a.displayId.split('-').pop(), 10);
            const numB = parseInt(b.displayId.split('-').pop(), 10);
            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB; // Sort Ascending by reference number
            }
        }
        // Fallback sort by generated date (newest first)
        return new Date(b.generatedAt) - new Date(a.generatedAt);
    });

  } catch (err) { 
    console.error("Storage list error:", err); 
    error.value = "Failed to load reports: " + err.message;
  } finally { 
    loading.value = false; 
  }
};

// eslint-disable-next-line no-unused-vars
const loadPdfUrl = async (fileItem) => { /* ... (same as before) ... */ 
  if (fileItem.downloadUrl || fileItem.urlError) return; 
  try {
    const url = await getDownloadURL(storageRef(fbStorage, fileItem.fullPath));
    const index = pdfFiles.value.findIndex(f => f.fullPath === fileItem.fullPath);
    if (index !== -1) { pdfFiles.value[index].downloadUrl = url; }
  } catch (err) {
    console.error("URL fetch error:", fileItem.fullPath, err);
    const index = pdfFiles.value.findIndex(f => f.fullPath === fileItem.fullPath);
    if (index !== -1) { pdfFiles.value[index].urlError = err.message || "Failed to load URL."; }
  }
};

// eslint-disable-next-line no-unused-vars
const attemptDownload = async (filePath) => { /* ... (same as before) ... */ 
  try {
    const url = await getDownloadURL(storageRef(fbStorage, filePath));
    window.open(url, '_blank'); 
  } catch (err) { alert('Could not get download URL.'); }
};

onMounted(() => { fetchPdfReports(); });
</script>

<style scoped>
.pdf-embed-container iframe {
  min-height: 600px; 
  height: 70vh; 
}
.font-mono { 
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>